import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, Search, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ContactsPage = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTipo, setFilterTipo] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchContacts();
  }, [filterTipo]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTipo) params.tipo_contacto = filterTipo;
      const res = await axios.get(`${API}/contacts`, { withCredentials: true, params });
      setContacts(res.data.items || res.data);
    } catch { toast.error('Error al cargar contactos'); }
    finally { setLoading(false); }
  };

  const filtered = search
    ? contacts.filter(c =>
        (c.nombre || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.email || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.telefono || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.asset_direccion || '').toLowerCase().includes(search.toLowerCase())
      )
    : contacts;

  const badgeColor = (tc) => {
    if (tc === 'GESTOR') return 'bg-blue-100 text-blue-700';
    if (tc === 'INVERSOR') return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-600';
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contacts-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="heading-1 flex items-center gap-2"><Users className="w-6 h-6 text-blue-600" />Contactos</h1>
      </div>

      {/* Filters */}
      <div className="card-base p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-[400px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar nombre, email, teléfono, activo..." className="input-base pl-9" data-testid="contacts-search" />
        </div>
        <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)} className="input-base w-auto" data-testid="contacts-filter-tipo">
          <option value="">Todos los tipos</option>
          <option value="OPERATIVO">Operativo</option>
          <option value="GESTOR">Gestor</option>
          <option value="INVERSOR">Inversor</option>
        </select>
        <span className="text-sm text-slate-500">{filtered.length} contactos</span>
      </div>

      {/* Table */}
      <div className="card-base overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-slate-500 text-center py-12">No hay contactos</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="contacts-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Nombre</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Tipo</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Teléfono</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Activo</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Estado</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Actualizado</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={`${c.id}-${c.asset_id}`} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => navigate(`/activos/${c.asset_id}`)} data-testid={`contact-global-row-${c.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{c.nombre}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badgeColor(c.tipo_contacto)}`}>{c.tipo_contacto || 'OPERATIVO'}</span>
                      {c.tipo && <span className="text-xs text-slate-400 ml-1">({c.tipo})</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{c.telefono || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{c.email || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-blue-600">
                        <Building2 className="w-3.5 h-3.5" />
                        <span className="truncate max-w-[200px]">{c.asset_direccion || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3"><span className="badge-neutral text-xs">{c.asset_estado || '-'}</span></td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{formatDate(c.updated_at || c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactsPage;
